import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../components/Button'
import { jeGreskaUcitavanjaModula } from './ucitavanje'

/**
 * Granica greške oko sva tri sučelja (CLAUDE.md §13).
 *
 * Povod je konkretan: rute se učitavaju lijeno, pa padne li dohvat modula
 * (prekinuta veza, 401 s brane pred statikom, chunk kojeg nakon deploya nema),
 * React odbaci stablo i korisnik gleda BIJELI EKRAN — bez poruke i bez ijednog
 * gumba. Bijeli ekran je najgori mogući odgovor: ne kaže je li kriva aplikacija,
 * mreža ili on, pa se recepcija javlja telefonom.
 *
 * Zato: poruka i "Osvježi". Osvježavanje je ovdje pravi lijek, a ne izlika —
 * navigacija ide mreža-prije-predmemorije (`public/sw.js`), pa novi `index.html`
 * donosi i nove hasheve chunkova.
 */
interface Props {
  children: ReactNode
}

interface Stanje {
  greska: unknown
}

export class GranicaGreske extends Component<Props, Stanje> {
  state: Stanje = { greska: null }

  static getDerivedStateFromError(greska: unknown): Stanje {
    return { greska }
  }

  componentDidCatch(greska: Error, podaci: ErrorInfo): void {
    // Konzola je jedino mjesto na koje se ovo može zapisati: telemetrije nema, a
    // greška bez traga je greška koju nitko ne može reproducirati.
    console.error('[shuttly] pad sučelja', greska, podaci.componentStack)
  }

  render(): ReactNode {
    if (this.state.greska) {
      return <PadEkran ucitavanje={jeGreskaUcitavanjaModula(this.state.greska)} />
    }

    return this.props.children
  }
}

/**
 * Ekran pada. Tamna paleta i kad je ispod nje svijetla konzola: ovo je poruka
 * sustava, a ne ekran modula — i mora izgledati isto svima (§17, tokeni).
 */
function PadEkran({ ucitavanje }: { ucitavanje: boolean }) {
  const { t } = useTranslation()

  return (
    <main role="alert" className="flex min-h-dvh items-center justify-center bg-noir px-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 text-center">
        <h1 className="text-2xl text-ivory">
          {t(ucitavanje ? 'state.chunkTitle' : 'state.crashTitle')}
        </h1>
        <p className="mt-2 text-sm text-stone">
          {t(ucitavanje ? 'state.chunkBody' : 'state.crashBody')}
        </p>
        <Button
          izgled="gold"
          className="mt-6 w-full"
          onClick={() => globalThis.location.reload()}
        >
          {t('common.refresh')}
        </Button>
      </div>
    </main>
  )
}
